import { Locale, TranslationKey, Translations } from './types';
import { ptBR } from './pt-BR';
import { en } from './en';
import { pl } from './pl';

export * from './types';

const dictionaries: Record<Locale, Translations> = {
  'pt-BR': ptBR,
  'en': en,
  'pl': pl,
};

let currentLocale: Locale = 'pt-BR';
const listeners: Array<(locale: Locale) => void> = [];

export function initI18n(): Locale {
  try {
    const saved = localStorage.getItem('slimelings_locale') as Locale | null;
    if (saved && (saved === 'pt-BR' || saved === 'en' || saved === 'pl')) {
      currentLocale = saved;
      return currentLocale;
    }
  } catch {
    // Ignore localStorage access issues in restricted contexts
  }

  // Detect browser language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('pl')) {
      currentLocale = 'pl';
    } else if (browserLang.startsWith('pt')) {
      currentLocale = 'pt-BR';
    } else {
      currentLocale = 'en';
    }
  }
  return currentLocale;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  if (locale !== 'pt-BR' && locale !== 'en' && locale !== 'pl') {
    throw new Error(`Unsupported locale: ${locale}`);
  }
  currentLocale = locale;
  try {
    localStorage.setItem('slimelings_locale', locale);
  } catch {
    // Ignore localStorage errors
  }
  for (const listener of listeners) {
    listener(currentLocale);
  }
}

export function onLocaleChange(listener: (locale: Locale) => void): () => void {
  listeners.push(listener);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

/**
 * Strict Translation function.
 * Per specification: "TODO E QUALQUER TEXTO DEVE SER i18n. NÃO USE FALLBACKS!"
 * If a key does not exist in the active dictionary, it throws an error immediately.
 */
export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  const dict = dictionaries[currentLocale];
  if (!dict) {
    throw new Error(`[i18n fatal] Dictionary not found for locale: ${currentLocale}`);
  }
  const text = dict[key];
  if (text === undefined || text === null) {
    throw new Error(`[i18n fatal] Missing key "${key}" in active locale "${currentLocale}". Fallbacks are strictly prohibited.`);
  }

  if (!params) return text;

  let result = text;
  for (const [k, v] of Object.entries(params)) {
    result = result.replaceAll(`{${k}}`, String(v));
  }
  return result;
}

export function getAllLocales(): Array<{ code: Locale; labelKey: TranslationKey; flag: string }> {
  return [
    { code: 'pt-BR', labelKey: 'lang_pt', flag: 'PT' },
    { code: 'en', labelKey: 'lang_en', flag: 'EN' },
    { code: 'pl', labelKey: 'lang_pl', flag: 'PL' },
  ];
}

export { ptBR, en, pl };
